import { useState, useCallback } from "react";
import {
  type ChangeDetails,
  type UseResetWarningModalOptions,
  type UseResetWarningModalReturn,
} from "./types";

export const useResetWarningModal = ({
  onConfirm,
  onCancel,
  onDirectChange,
  getDocumentName,
  getCurrentValue,
  getReadableValue,
  changeType,
  shouldShowModal,
}: UseResetWarningModalOptions): UseResetWarningModalReturn => {
  const [showModal, setShowModal] = useState(false);
  const [pendingChange, setPendingChange] = useState<ChangeDetails | null>(null);
  const [pendingNewValue, setPendingNewValue] = useState<string>("");
  const [previousValue, setPreviousValue] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);

  const handleFieldChange = useCallback(
    (newValue: string) => {
      const currentValue = getCurrentValue();
      const shouldShow = shouldShowModal();
      const hasValueChanged = currentValue && currentValue !== newValue;

      if (hasValueChanged && shouldShow) {
        const changeDetails: ChangeDetails = {
          changeType,
          currentValue: getReadableValue(currentValue),
          newValue: getReadableValue(newValue),
          documentName: getDocumentName(),
        };
        setPendingChange(changeDetails);
        setPendingNewValue(newValue);
        setPreviousValue(currentValue);
        setShowModal(true);
      } else {
        onDirectChange(newValue);
      }
    },
    [
      changeType,
      getCurrentValue,
      getDocumentName,
      getReadableValue,
      onDirectChange,
      shouldShowModal,
    ],
  );

  const handleConfirm = useCallback(async () => {
    setIsLoading(true);
    try {
      const success = await onConfirm(pendingNewValue);
      if (success) {
        setShowModal(false);
        setPendingChange(null);
        setPendingNewValue("");
        setPreviousValue("");
      }
    } finally {
      setIsLoading(false);
    }
  }, [onConfirm, pendingNewValue]);

  const handleCancel = useCallback(() => {
    setShowModal(false);
    onCancel(previousValue);
    setPendingChange(null);
    setPendingNewValue("");
    setPreviousValue("");
  }, [onCancel, previousValue]);

  return {
    showModal,
    pendingChange,
    isLoading,
    handleFieldChange,
    handleConfirm,
    handleCancel,
  };
};
